import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import { filter } from 'rxjs';
import { CustomGenericDataFetchComponent } from '../custom-generic-data-fetch/custom-generic-data-fetch.component';
import { EGenericInputTriggerType } from '../../../models/irembo-generic-input-status.enum';
import { IEnvironment } from '../../../models/environment.model';
import {
  FormStateService,
  FetchSuccessEvent,
} from '../../../services/formly/form-state.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

type ConditionOperator =
  | 'EQ'
  | 'NEQ'
  | 'GT'
  | 'LT'
  | 'GTE'
  | 'LTE'
  | 'CONTAINS';

interface FetchCondition {
  field: string;
  operator: ConditionOperator;
  value: any;
  searchInRootForm?: boolean;
}

@Component({
  standalone: false,
  selector: 'irembogov-custom-conditional-data-fetch',
  templateUrl: './custom-conditional-data-fetch.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CustomConditionalDataFetchComponent
  extends CustomGenericDataFetchComponent
  implements OnDestroy, AfterViewInit
{
  private fetchSuccessMap: Map<string, boolean> = new Map();
  private pendingFetchQueue: any[] = [];

  constructor(
    http: HttpClient,
    cd: ChangeDetectorRef,
    formStateService: FormStateService,
    modalService: NgbModal,
    @Inject('environment') environment: IEnvironment
  ) {
    super(http, cd, formStateService, modalService, environment);
  }

  override ngAfterViewInit(): void {
    // For ON_SUCCESS triggers, manually initialize repeater context
    // For other triggers, super.ngAfterViewInit() will handle it
    if (
      this.field.props['triggerType'] === EGenericInputTriggerType.ON_SUCCESS
    ) {
      // Initialize repeater context (needed for findControl in conditions)
      this.initializeRepeaterContext();

      // Set fieldsToPopulate (needed for populate functionality)
      this.fieldsToPopulate = this.field.props?.['populates'] ?? [];
    } else {
      // Call parent's full initialization (includes initializeRepeaterContext)
      super.ngAfterViewInit();
    }

    this.registerWithFetchCoordinator();
    this.subscribeToPredecessorEvents();
  }

  private registerWithFetchCoordinator(): void {
    if (this.field.key) {
      this.formStateService.registerFetchComponent(
        this.field.key as string,
        this.componentId
      );
    }
  }

  private subscribeToPredecessorEvents(): void {
    const onSuccessOf = this.field.props['onSuccessOf'];

    if (!onSuccessOf) {
      return;
    }

    this.subscriptions.add(
      this.formStateService.fetchSuccess$
        .pipe(
          filter((event: FetchSuccessEvent) => event.fetchKey === onSuccessOf)
        )
        .subscribe(() => {
          // Mark predecessor as complete
          this.fetchSuccessMap.set(onSuccessOf, true);

          // If this is ON_SUCCESS trigger, execute fetch immediately
          if (
            this.field.props['triggerType'] ===
            EGenericInputTriggerType.ON_SUCCESS
          ) {
            // Let parent's buildPayload handle payload construction
            // Pass empty string - parent will build from payloadKeyMappings or payloadKey
            this.onDataFetch('');
          } else {
            // For FIELD_LISTENER, check if there are pending fetches
            this.checkAndExecutePendingFetch();
          }
        })
    );
  }

  override onDataFetch(inputValue: string): void {
    // Gate 1: For FIELD_LISTENER, check if predecessor is complete
    if (
      this.field.props['triggerType'] ===
      EGenericInputTriggerType.FIELD_LISTENER
    ) {
      const onSuccessOf = this.field.props['onSuccessOf'];
      if (onSuccessOf && !this.isPredecessorComplete(onSuccessOf)) {
        // Queue the fetch for later
        this.pendingFetchQueue = [inputValue]; // Keep only the most recent
        return;
      }
    }

    // Gate 2: Check fetchCondition
    if (!this.evaluateFetchCondition()) {
      return;
    }

    // Proceed with the fetch
    super.onDataFetch(inputValue);
  }

  private evaluateFetchCondition(): boolean {
    const fetchCondition = this.field.props['fetchCondition'] as FetchCondition;

    if (!fetchCondition) {
      return true; // No condition means always proceed
    }

    // Find the control to check
    const control = this.findControl(
      fetchCondition.field,
      fetchCondition.searchInRootForm
    );

    if (!control) {
      return false; // Cannot evaluate condition if control not found
    }

    const result = this.compareValues(
      control.value,
      fetchCondition.value,
      fetchCondition.operator
    );

    return result;
  }

  private compareValues(
    controlValue: any,
    conditionValue: any,
    operator: ConditionOperator
  ): boolean {
    switch (operator) {
      case 'EQ':
        return controlValue === conditionValue;
      case 'NEQ':
        return controlValue !== conditionValue;
      case 'GT':
        return controlValue > conditionValue;
      case 'LT':
        return controlValue < conditionValue;
      case 'GTE':
        return controlValue >= conditionValue;
      case 'LTE':
        return controlValue <= conditionValue;
      case 'CONTAINS':
        if (
          typeof controlValue === 'string' &&
          typeof conditionValue === 'string'
        ) {
          return controlValue.includes(conditionValue);
        }
        if (Array.isArray(controlValue)) {
          return controlValue.includes(conditionValue);
        }
        return false;
      default:
        return false;
    }
  }

  private isPredecessorComplete(fetchKey: string): boolean {
    return this.fetchSuccessMap.get(fetchKey) === true;
  }

  private checkAndExecutePendingFetch(): void {
    if (this.pendingFetchQueue.length > 0) {
      // Get the most recent queued value
      const mostRecentValue =
        this.pendingFetchQueue[this.pendingFetchQueue.length - 1];
      this.pendingFetchQueue = [];

      // Check if conditions are now met
      if (this.evaluateFetchCondition()) {
        const onSuccessOf = this.field.props['onSuccessOf'];
        if (!onSuccessOf || this.isPredecessorComplete(onSuccessOf)) {
          super.onDataFetch(mostRecentValue);
        }
      }
    }
  }

  override ngOnDestroy(): void {
    // Unregister from fetch coordinator
    if (this.field?.key) {
      this.formStateService.unregisterFetchComponent(this.field.key as string);
    }

    // Call parent cleanup
    super.ngOnDestroy();
  }
}
